import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MdPets } from 'react-icons/md';
import { login, resetAuth } from '../../store/slices/authSlice';
import authService from '../../services/authService';

const EMAIL_REGEX = /^\S+@\S+\.\S+$/;

const getErrorMessage = (error, fallback) =>
  error.response?.data?.message || error.response?.data?.error || error.message || fallback;

const Login = () => {
  // 'login' | 'forgot' | 'reset'
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const switchMode = (nextMode) => {
    dispatch(resetAuth());
    setMode(nextMode);
    setPassword('');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!EMAIL_REGEX.test(email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!password) {
      toast.error('Please enter your password');
      return;
    }
    const result = await dispatch(login({ email: email.trim(), password }));
    if (login.fulfilled.match(result)) {
      toast.success('Login successful!');
      navigate('/dashboard');
    }
  };

  const handleSendResetOtp = async (e) => {
    if (e) e.preventDefault();
    if (!EMAIL_REGEX.test(email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await authService.forgotPassword(email.trim());
      setOtpMessage(response.message);
      toast.success(response.message);
      setMode('reset');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to send OTP'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    if (newPassword.length < 8 || !/[A-Za-z]/.test(newPassword) || !/\d/.test(newPassword)) {
      toast.error('Password must be at least 8 characters with a letter and a number');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await authService.resetPassword(email.trim(), otp, newPassword);
      toast.success(response.message);
      switchMode('login');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to reset password'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const linkButtonStyle = {
    background: 'none',
    border: 'none',
    padding: 0,
    color: 'var(--primary)',
    cursor: 'pointer',
    fontSize: '13px',
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <MdPets />
          </div>
          <h1>Pet Admin</h1>
          <p>
            {mode === 'login'
              ? 'Sign in to access the admin dashboard'
              : 'Reset your admin password'}
          </p>
        </div>

        {mode === 'login' && (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-control"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div style={{ textAlign: 'right', marginBottom: '16px' }}>
              <button type="button" style={linkButtonStyle} onClick={() => switchMode('forgot')}>
                Forgot password?
              </button>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%' }}
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleSendResetOtp}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                placeholder="Enter your admin email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                An OTP will be sent to the mobile number registered with this account
              </p>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: '12px' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send OTP'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: '100%' }}
              onClick={() => switchMode('login')}
            >
              Back to Login
            </button>
          </form>
        )}

        {mode === 'reset' && (
          <form onSubmit={handleResetPassword}>
            <div className="form-group">
              <label className="form-label">Enter OTP</label>
              <input
                type="text"
                inputMode="numeric"
                className="form-control"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                maxLength={6}
                autoComplete="one-time-code"
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                {otpMessage}{' '}
                <button
                  type="button"
                  style={linkButtonStyle}
                  onClick={() => handleSendResetOtp()}
                  disabled={isSubmitting}
                >
                  Resend OTP
                </button>
              </p>
            </div>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className="form-control"
                placeholder="At least 8 characters, a letter and a number"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password"
                className="form-control"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: '12px' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Resetting...' : 'Reset Password'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ width: '100%' }}
              onClick={() => switchMode('login')}
            >
              Back to Login
            </button>
          </form>
        )}

        <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '24px', textAlign: 'center' }}>
          Only admin users can access this dashboard
        </p>
      </div>
    </div>
  );
};

export default Login;
